import { useEffect, useRef, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { ChevronLeft, LogOut, Menu } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/auth/auth-provider'
import { useConfirmLeave } from '@/components/shared/unsaved-changes'
import { appConfig } from '@/lib/config'
import { getRoleExperience } from '@/lib/role-experience'
import { isCeo } from '@/lib/access-control'
import { getVisibleNavigation } from '@/lib/workspace-navigation'
import { WorkspaceNavigation } from './workspace-navigation'
import { WorkspaceSearch } from './workspace-search'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { GuideCenter } from '@/components/tours/guide-center'
import { DailyClockInPrompt } from '@/components/attendance/daily-clock-in-prompt'
import { FirstLoginTaskReminder } from '@/components/tasks/first-login-task-reminder'
import { cn, initials } from '@/lib/utils'

const splashDurationMs = 1400
const splashSessionKey = 'workhub-welcome-shown'

export function AppShell() {
  const location = useLocation()
  const isAiPage = location.pathname === '/ai'
  const menuButtonRef = useRef<HTMLSpanElement>(null)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const [showSplash, setShowSplash] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.sessionStorage.getItem(splashSessionKey) !== '1'
  })
  const { access, employee, signOut } = useAuth()
  const confirmLeave = useConfirmLeave()
  const experience = getRoleExperience(access)
  const visibleGroups = getVisibleNavigation(access)

  useEffect(() => {
    setMobileOpen(false)
  }, [location.key])

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 1024px)')
    const closeMobileOnDesktop = () => {
      if (desktop.matches) setMobileOpen(false)
    }
    desktop.addEventListener('change', closeMobileOnDesktop)
    return () => desktop.removeEventListener('change', closeMobileOnDesktop)
  }, [])

  useEffect(() => {
    if (!showSplash) return
    window.sessionStorage.setItem(splashSessionKey, '1')
    const timeout = window.setTimeout(() => setShowSplash(false), splashDurationMs)
    return () => window.clearTimeout(timeout)
  }, [showSplash])

  const sidebar = (compact: boolean, mobile = false) => (
    <div className="flex h-full flex-col border-r border-white/5 bg-[#0f110e] text-slate-100">
      <div className={cn('flex h-16 items-center gap-3 border-b border-white/5 px-4', compact && 'justify-center', mobile && 'pr-14')}>
        <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-primary to-secondary font-black text-white shadow-md shadow-primary/20">W</div>
        {!compact && (
          <div className="min-w-0 flex-1">
            <div className="truncate font-bold leading-tight tracking-wide text-white">{appConfig.appName}</div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#3cac00]">{experience.sidebarLabel}</div>
          </div>
        )}
      </div>
      <WorkspaceNavigation
        groups={visibleGroups}
        pathname={location.pathname}
        navigationKey={location.key}
        collapsed={compact}
        onExpand={() => setCollapsed(false)}
        onNavigate={() => setMobileOpen(false)}
      />
      <div className="border-t border-white/5 p-3">
        <button
          type="button"
          aria-label="Sign out"
          onClick={() => confirmLeave(() => void signOut())}
          className={cn(
            'group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-400 transition-colors hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
            compact && 'justify-center px-2',
          )}
          title={compact ? 'Sign out' : undefined}
        >
          <LogOut aria-hidden="true" className="size-5 transition-transform group-hover:translate-x-0.5" />
          <span className={compact ? 'sr-only' : undefined}>Sign out</span>
        </button>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-background">
      <DailyClockInPrompt />
      <FirstLoginTaskReminder />
      {showSplash && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center overflow-hidden bg-background px-6">
          <div className="splash-glow splash-glow-primary absolute left-[12%] top-[14%] size-72 rounded-full bg-primary/15 blur-3xl" />
          <div className="splash-glow splash-glow-secondary absolute bottom-[12%] right-[14%] size-80 rounded-full bg-secondary/15 blur-3xl" />
          <div className="splash-card-enter relative w-full max-w-lg rounded-[2.5rem] border border-white/90 bg-white/90 p-8 text-center shadow-2xl shadow-primary/5 backdrop-blur-md">
            <div className="splash-logo mx-auto flex size-14 items-center justify-center rounded-2xl bg-gradient-to-tr from-primary to-secondary text-2xl font-black text-white shadow-lg">W</div>
            <p className="mt-6 text-xs font-bold uppercase tracking-[0.24em] text-primary">{experience.workspaceTitle}</p>
            <h2 className="mt-3 text-xl font-extrabold tracking-tight text-foreground">{experience.splashTitle}</h2>
            <p className="mt-3 text-4xl font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary sm:text-5xl animate-fade-in">{access?.fullName || 'WINBOX'}</p>
            <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-muted-foreground">{experience.splashMessage}</p>
            <div className="mx-auto mt-6 h-1.5 w-44 overflow-hidden rounded-full bg-primary/10">
              <div className="h-full rounded-full bg-gradient-to-r from-primary to-secondary" style={{ animation: `splash-progress ${splashDurationMs}ms ease-out forwards` }} />
            </div>
          </div>
        </div>
      )}
      <aside className={cn('fixed inset-y-0 left-0 z-40 hidden transition-all duration-300 ease-in-out lg:block', collapsed ? 'w-20' : 'w-64')}>{sidebar(collapsed)}</aside>
      <Dialog open={mobileOpen} onOpenChange={setMobileOpen}>
        <DialogContent
          className="inset-y-0 left-0 top-0 h-dvh max-h-none w-72 max-w-[calc(100%-2rem)] translate-x-0 translate-y-0 gap-0 overflow-hidden rounded-none border-0 p-0 text-slate-100 lg:hidden"
          onEscapeKeyDown={() => setMobileOpen(false)}
          onPointerDownOutside={() => setMobileOpen(false)}
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            menuButtonRef.current?.querySelector('button')?.focus()
          }}
        >
          <DialogTitle className="sr-only">Workspace navigation</DialogTitle>
          <DialogDescription className="sr-only">Choose a page or expand a group to see more destinations.</DialogDescription>
          {sidebar(false, true)}
        </DialogContent>
      </Dialog>
      <div className={cn('transition-all duration-300 ease-in-out', collapsed ? 'lg:pl-20' : 'lg:pl-64')}>
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b bg-background/80 px-3 backdrop-blur-md sm:px-6">
          <div className="flex items-center gap-2">
            <span ref={menuButtonRef} className="lg:hidden">
              <Button type="button" variant="ghost" size="icon" aria-label="Open navigation menu" aria-expanded={mobileOpen} aria-haspopup="dialog" className="hover:bg-muted" onClick={() => setMobileOpen(true)}>
                <Menu aria-hidden="true" className="size-5" />
              </Button>
            </span>
            <Button type="button" variant="ghost" size="icon" aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} aria-expanded={!collapsed} className="hidden lg:inline-flex hover:bg-muted" onClick={() => setCollapsed((value) => !value)}>
              <ChevronLeft aria-hidden="true" className={cn('size-5 transition duration-200', collapsed && 'rotate-180')} />
            </Button>
            <WorkspaceSearch groups={visibleGroups} />
          </div>
          <div className="flex min-w-0 items-center gap-2 sm:gap-4">
            {access ? (
              <GuideCenter
                userId={access.id}
                access={{
                  isSuperAdmin: access.isSuperAdmin,
                  permissions: access.permissions,
                  roles: access.roles,
                }}
              />
            ) : null}
            {isCeo(access) ? (
              <Badge variant="outline" className="hidden border-primary/20 bg-primary/5 text-primary sm:inline-flex">Chief Executive Officer</Badge>
            ) : access?.isSuperAdmin ? (
              <Badge variant="outline" className="hidden border-primary/20 bg-primary/5 text-primary sm:inline-flex">Super Admin</Badge>
            ) : null}
            <div className="hidden text-right sm:block">
              <p className="text-sm font-semibold leading-none text-foreground">{access?.fullName}</p>
              <p className="mt-1 text-[11px] font-medium text-muted-foreground">{employee?.employeeProfile?.jobTitle || 'Employee'}</p>
            </div>
            <div className="flex size-9 items-center justify-center rounded-full bg-gradient-to-tr from-primary to-secondary text-sm font-bold text-white shadow-sm">{initials(access?.fullName)}</div>
          </div>
        </header>
        <main className={cn("mx-auto w-full max-w-[1600px] p-4 sm:p-6 lg:p-8 animate-in fade-in-50 duration-300", isAiPage ? "h-[calc(100dvh-4rem)] overflow-hidden" : "space-y-6")}><Outlet /></main>
      </div>
    </div>
  )
}
